import {
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from "antd";
import { useState } from "react";
import { useAuth } from "../../../../hooks/useAuth";
import { GENDER } from "../../../../constants/common";

export function EventAttendanceList({
  data = [],
  loading,
  filter = { page: 1, limit: 10 },
  pagination,
  onTableChange,
  onCheckIn,
  checkInLoading,
  onStatusChange,
  onDelete,
  deleteLoading,
  onUpdateRegistrationCode,
  updateCodeLoading,
  onTakingRefugeChange,
  takingRefugeLoading,
  user,
  event,
}) {
  const { data: currentUser } = useAuth();
  const userRole = currentUser?.role || user?.role;
  const isHoaDangEvent = event?.eventType === "hoa-dang";
  const hasRefugeCeremony = event?.hasRefugeCeremony;

  const [previewImage, setPreviewImage] = useState(null);
  const [editCodeModalOpen, setEditCodeModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [newCode, setNewCode] = useState("");

  const columns = [
    {
      title: "STT",
      key: "name",
      render: (_, __, index) => (filter.page - 1) * filter.limit + index + 1,
      width: 60,
      align: "center",
      responsive: ["xs", "sm", "md", "lg", "xl"],
    },

    {
      title: "Tên",
      dataIndex: ["user", "name"],
      key: "name",
      minWidth: 200,
      responsive: ["xs", "sm", "md", "lg", "xl"],
      render: (name, record) => (
        <Space>
          <Avatar
            src={record.avatarUrl || record.user?.avatar}
            size="large"
            style={{ width: 50, height: 50, cursor: "pointer" }}
            onClick={() =>
              setPreviewImage(record.avatarUrl || record.user?.avatar)
            }
          />
          <div>
            <strong style={{ padding: 0, margin: 0 }}>{name}</strong>
            <p
              style={{
                padding: 0,
                margin: 0,
                fontStyle: "italic",
                color: "grey",
              }}
            >
              {record.user.email}
            </p>
          </div>
        </Space>
      ),
    },

    {
      title: "Qr code",
      dataIndex: "qrCode",
      key: "qrCode",
      minWidth: 200,
      responsive: ["md", "lg", "xl"],
      render: (qrCode) =>
        qrCode ? (
          <Space>
            <span style={{ fontFamily: "monospace" }}>{qrCode}</span>
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => {
                navigator.clipboard.writeText(qrCode);
                message.success("Đã sao chép mã QR");
              }}
              size="small"
            />
          </Space>
        ) : null,
    },
    {
      title: "Mã tham dự",
      dataIndex: "registrationCode",
      key: "registrationCode",
      align: "center",
      minWidth: 130,
      render: (registrationCode, record) => (
        <Space>
          <Tag variant="outlined" color="red-inverse">
            {registrationCode}
          </Tag>
          {userRole === "admin" && (
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedRecord(record);
                setNewCode(registrationCode || "");
                setEditCodeModalOpen(true);
              }}
            />
          )}
        </Space>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const statusConfig = {
          registered: { color: "blue", text: "Đã đăng ký" },
          cancelled: { color: "red", text: "Đã hủy" },
        };
        const config = statusConfig[status] || {
          color: "default",
          text: status,
        };

        if (userRole === "admin") {
          return (
            <Select
              value={status}
              onChange={(value) => onStatusChange(record._id, value)}
              style={{ width: 120 }}
              options={[
                { value: "pending", label: "Chờ xác nhận" },
                { value: "registered", label: "Đã đăng ký" },
                { value: "attended", label: "Đã check-in" },
                { value: "cancelled", label: "Đã hủy" },
                { value: "completed", label: "Đã check-out" },
                { value: "rejected", label: "Đã từ chối" },
              ]}
            />
          );
        }

        return <Tag color={config.color}>{config.text}</Tag>;
      },
      responsive: ["xs", "sm", "md", "lg", "xl"],
    },
    {
      title: "Giới tính",
      dataIndex: ["user", "gender"],
      key: ["user", "gender"],
      align: "center",
      minWidth: 100,
      render: (gender) => (
        <Tag variant="outlined" color="yellow">
          {GENDER[gender]}
        </Tag>
      ),
    },
    {
      title: "Địa chỉ",
      dataIndex: ["user", "address"],
      key: ["user", "address"],
      minWidth: 250,
    },
    {
      title: "Ngày sinh",
      dataIndex: ["user", "birthday"],
      key: "birthday",
      minWidth: 150,
      responsive: ["lg", "xl"],
      align: "center",
      render: (date) => new Date(date).toLocaleDateString("vi-VN"),
    },
    {
      title: "Số điện thoại",
      dataIndex: ["user", "phone"],
      key: "phone",
      minWidth: 150,
      responsive: ["lg", "xl"],
    },
    {
      title: "Xác thực email",
      dataIndex: ["user", "isVerified"],
      key: "isVerified",
      minWidth: 100,
      align: "center",
      render: (isVerified) => (
        <Tag color={isVerified ? "green" : "red"}>
          {isVerified ? "Đã xác thực" : "Chưa xác thực"}
        </Tag>
      ),
      responsive: ["md", "lg", "xl"],
    },

    {
      title: "Ngày đăng ký",
      dataIndex: "registrationDate",
      key: "registrationDate",
      minWidth: 150,
      align: "center",
      render: (date) => new Date(date).toLocaleDateString("vi-VN"),
      responsive: ["sm", "md", "lg", "xl"],
    },
    {
      title: "Trạng thái mượn/mua áo",
      dataIndex: "costume",
      key: "costume",
      minWidth: 150,
      align: "center",
      render: (costume) => {
        return (
          <Tag color={costume?.type === "borrow" ? "blue" : "green"}>
            {!costume?.type ? "" : costume?.type === "borrow" ? "Mượn" : "Mua"}
          </Tag>
        );
      },
    },
    {
      title: "Trạng thái mua/mượn áo",
      key: "costume_status",
      minWidth: 150,
      render: (_, record) => {
        const status =
          record?.costume?.type === "borrow"
            ? record?.costume?.borrowStatus
            : record?.costume?.buyStatus;

        const statusOptions =
          record?.costume?.type === "borrow"
            ? [
                { value: "pending", label: "Chờ nhận" },
                { value: "borrowed", label: "Đã mượn" },
                { value: "returned", label: "Đã trả" },
                { value: "lost", label: "Mất" },
                { value: "cancelled", label: "Đã hủy" },
              ]
            : [
                { value: "paid", label: "Đã thanh toán" },
                { value: "unpaid", label: "Chưa thanh toán" },
                { value: "cancelled", label: "Đã hủy" },
              ];
        const statusLabel =
          statusOptions.find((opt) => opt.value === status)?.label || status;
        return (
          <Tag
            color={
              status === "paid" || status === "returned"
                ? "green"
                : status === "cancelled"
                  ? "red"
                  : "blue"
            }
          >
            {statusLabel}
          </Tag>
        );
      },
    },
    ...(isHoaDangEvent
      ? [
          {
            title: "Nghề nghiệp",
            dataIndex: ["user", "occupation"],
            key: ["user", "occupation"],
            minWidth: 150,
            responsive: ["lg", "xl"],
          },
        ]
      : []),

    ...(event?.eventType === "thien-tra"
      ? [
          {
            title: "Pháp danh",
            dataIndex: "buddhistName",
            key: "buddhistName",
            minWidth: 150,
            render: (buddhistName) => buddhistName || "-",
          },
        ]
      : []),

    ...(hasRefugeCeremony
      ? [
          {
            title: "Quy Y Tam Bảo",
            dataIndex: "isTakingRefuge",
            key: "isTakingRefuge",
            minWidth: 200,
            align: "center",
            responsive: ["sm", "md", "lg", "xl"],
            render: (isTakingRefuge, record) =>
              userRole === "admin" ? (
                <Switch
                  checked={isTakingRefuge}
                  onChange={(checked) =>
                    onTakingRefugeChange(record._id, checked)
                  }
                  loading={takingRefugeLoading}
                />
              ) : isTakingRefuge ? (
                <Tag color="green">Tham dự</Tag>
              ) : null,
          },
        ]
      : []),

    {
      title: "Lý do hủy",
      dataIndex: "cancelReason",
      key: "cancelReason",
      minWidth: 200,
      align: "center",
      responsive: ["sm", "md", "lg", "xl"],
    },
    {
      title: "Thời gian check-in",
      dataIndex: "checkInTime",
      key: "checkInTime",
      minWidth: 150,
      align: "center",
      render: (time) => (time ? new Date(time).toLocaleString("vi-VN") : "-"),
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Hành động",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => onCheckIn(record)}
            loading={checkInLoading}
            disabled={record.status !== "registered"}
          >
            Check-in
          </Button>
          {userRole === "admin" && (
            <Popconfirm
              title="Xóa đăng ký"
              description="Bạn có chắc chắn muốn xóa đăng ký này không?"
              onConfirm={() => onDelete(record._id)}
              okText="Có"
              cancelText="Không"
            >
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deleteLoading}
              />
            </Popconfirm>
          )}
        </Space>
      ),
      responsive: ["xs", "sm", "md", "lg", "xl"],
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={data}
        scroll={{ x: 1600 }}
        rowKey="_id"
        loading={loading}
        bordered
        size="small"
        pagination={{
          current: filter.page,
          pageSize: filter.limit,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} của ${total} mục`,
        }}
        onChange={onTableChange}
      />

      <Modal
        open={!!previewImage}
        title="Ảnh đại diện"
        footer={null}
        onCancel={() => setPreviewImage(null)}
        width={400}
      >
        {previewImage && (
          <img
            src={previewImage}
            alt="Avatar"
            style={{ width: "100%", display: "block" }}
          />
        )}
      </Modal>

      <Modal
        open={editCodeModalOpen}
        title="Cập nhật mã tham dự"
        onCancel={() => setEditCodeModalOpen(false)}
        onOk={async () => {
          if (!selectedRecord || !newCode.trim()) return;
          await onUpdateRegistrationCode?.(selectedRecord._id, newCode.trim());
          setEditCodeModalOpen(false);
          setSelectedRecord(null);
        }}
        confirmLoading={updateCodeLoading}
        okText="Cập nhật"
        cancelText="Hủy"
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="Mã tham dự mới">
            <Input
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              placeholder="Nhập mã tham dự mới"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
